import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { isLoggedInAtom, userInfoAtom } from 'app/atoms';
import { getI18nLanguage } from 'app/lib/language';
import { useAllGroups } from './groups';
import { useAllAddresses } from './address';
import {
  getMyListingsOffersFilters,
  getMyListingsRequestsFilters,
  normalizeListingFilters,
} from './listing-query-filters';
import {
  addressKeys,
  bonificationKeys,
  channelKeys,
  listingKeys,
  loanKeys,
  messageKeys,
  notificationKeys,
  planKeys,
  preferencesKeys,
  profileKeys,
  userKeys,
} from './keys';
import { statisticsKeys } from './statistics';
import type { FindAllListingsDTO, Listing } from 'app/types/listing';
import type { Language } from 'app/lib/language';
import type { Loan } from 'app/types/loan';
import type { Bonification } from 'app/types/bonification';
import type { PreferenceName } from 'app/types/preferences';
import type { User } from 'app/types/user';
import type { Profile } from 'app/types/profile';
import { type Channel, getOneChannelDefaultInclude, type CustomChannelType } from 'app/types/chat';

export function useQueryKeys() {
  const client = useQueryClient();
  const keys = useGetQueryKeys();

  return (() => {
    const i = (queryKey: readonly unknown[]) => client.invalidateQueries({ queryKey });

    // === LISTINGS ===
    const allListings = { invalidate: () => i(keys.allListings()), key: keys.allListings() };
    const listing = {
      invalidate: (id: Listing['id']) => i(keys.listing(id)),
      key: (id: Listing['id']) => keys.listing(id),
    };
    const listingDetailMany = (ids: Listing['id'][]) => i(keys.listingDetailMany(ids));
    const allListingLists = { invalidate: () => i(keys.allListingLists()), key: keys.allListingLists() };
    const myOffers = { invalidate: () => i(keys.myOffers()), key: keys.myOffers() };
    const myRequests = { invalidate: () => i(keys.myRequests()), key: keys.myRequests() };
    const favourites = { invalidate: () => i(keys.favourites()), key: keys.favourites() };
    const groupingFavourites = { invalidate: () => i(keys.groupingFavourites()), key: keys.groupingFavourites() };
    const urgentNeeds = { invalidate: () => i(keys.urgentNeeds()), key: keys.urgentNeeds() };
    const listingsWithFilters = (filters: FindAllListingsDTO & { isLoggedIn?: boolean; userLanguage?: Language }) =>
      i(keys.listingsWithFilters(filters));

    // === LOANS ===
    const allLoans = { invalidate: () => i(keys.allLoans()), key: keys.allLoans() };
    const loan = { invalidate: (id: Loan['id']) => i(keys.loan(id)), key: (id: Loan['id']) => keys.loan(id) };
    const ratings = {
      invalidate: (filters?: { userId?: User['id']; listingId?: Listing['id']; reviewerId?: User['id'] }) =>
        i(keys.ratings(filters)),
      key: (filters?: { userId?: User['id']; listingId?: Listing['id']; reviewerId?: User['id'] }) =>
        keys.ratings(filters),
    };

    // === CHANNELS / MESSAGES ===
    const allChannelLists = { invalidate: () => i(keys.allChannelLists()), key: keys.allChannelLists() };
    const channelList = {
      invalidate: (filters?: {
        search?: string;
        type?: CustomChannelType;
        archived?: boolean;
        listingId?: string;
        userId?: string;
      }) => i(keys.channelList(filters)),
      key: (filters?: {
        search?: string;
        type?: CustomChannelType;
        archived?: boolean;
        listingId?: string;
        userId?: string;
      }) => keys.channelList(filters),
    };
    const activeChannels = {
      invalidate: (listingId?: string, userId?: string) => i(keys.activeChannels(listingId, userId)),
      key: (listingId?: string, userId?: string) => keys.activeChannels(listingId, userId),
    };
    const archivedChannels = {
      invalidate: (listingId?: string, userId?: string) => i(keys.archivedChannels(listingId, userId)),
      key: (listingId?: string, userId?: string) => keys.archivedChannels(listingId, userId),
    };
    const channel = { invalidate: (url: string) => i(keys.channel(url)), key: (url: string) => keys.channel(url) };
    const channelDetail = {
      invalidate: (url: string) => i(keys.channelDetail(url)),
      key: (url: string) => keys.channelDetail(url),
    };
    const allChannelDetails = { invalidate: () => i(keys.allChannelDetails()), key: keys.allChannelDetails() };
    const unreadMessages = { invalidate: () => i(keys.unreadMessages()), key: keys.unreadMessages() };

    // === USER / PROFILE ===
    const selfUser = { invalidate: () => i(keys.selfUser()), key: keys.selfUser() };
    const allUserLists = { invalidate: () => i(keys.allUserLists()), key: keys.allUserLists() };
    const profile = {
      invalidate: (userId: Profile['id']) => i(keys.profile(userId)),
      key: (userId: Profile['id']) => keys.profile(userId),
    };

    // === ADDRESSES ===
    const myAddresses = { invalidate: () => i(keys.myAddresses()), key: keys.myAddresses() };

    // === NOTIFICATIONS ===
    const allNotifications = { invalidate: () => i(keys.allNotifications()), key: keys.allNotifications() };
    const unreadNotifications = { invalidate: () => i(keys.unreadNotifications()), key: keys.unreadNotifications() };

    // === BONIFICATIONS ===
    const bonification = {
      invalidate: (id: Bonification['id']) => i(keys.bonification(id)),
      key: (id: Bonification['id']) => keys.bonification(id),
    };
    const pendingBonifications = { invalidate: () => i(keys.pendingBonifications()), key: keys.pendingBonifications() };

    // === PREFERENCES ===
    const preference = {
      invalidate: (name: PreferenceName) => i(keys.preference(name)),
      key: (name: PreferenceName) => keys.preference(name),
    };

    // === STATISTICS ===
    const statistics = {
      invalidate: (userId: User['id']) => i(keys.statistics(userId)),
      key: (userId: User['id']) => keys.statistics(userId),
    };

    // === PLANS ===
    const allPlans = { invalidate: () => i(keys.allPlans()), key: keys.allPlans() };

    // === CROSS-DOMAIN CASCADES ===
    const afterListingCreated = (result: Pick<Listing, 'userId'>) =>
      Promise.all([selfUser.invalidate(), allListingLists.invalidate(), statistics.invalidate(result.userId)]);
    const afterListingDeleted = (listingId: Listing['id']) =>
      Promise.all([listing.invalidate(listingId), allListingLists.invalidate()]);
    const afterLoanCreated = () => Promise.all([allLoans.invalidate()]);
    const afterLoanAccepted = (result: Pick<Loan, 'channelURL'>) =>
      Promise.all([allLoans.invalidate(), channel.invalidate(result.channelURL), allPlans.invalidate()]);
    const afterLoanCompleted = (result: Pick<Loan, 'channelURL'>) =>
      Promise.all([allLoans.invalidate(), channel.invalidate(result.channelURL), allPlans.invalidate()]);
    const afterLoanRejected = (result: Pick<Loan, 'channelURL' | 'listingId'>) =>
      Promise.all([
        allLoans.invalidate(),
        channel.invalidate(result.channelURL),
        listing.invalidate(result.listingId),
        allListingLists.invalidate(),
        allPlans.invalidate(),
      ]);
    const afterAddressChanged = () => Promise.all([myAddresses.invalidate(), allUserLists.invalidate()]);
    const afterBonificationCreated = (channelUrl?: Channel['channel_url']) =>
      Promise.all([channelUrl ? channelDetail.invalidate(channelUrl) : Promise.resolve(), activeChannels.invalidate()]);
    const afterBonificationAccepted = (
      id: Bonification['id'],
      listingId: Listing['id'],
      channelUrl?: Channel['channel_url'],
    ) =>
      Promise.all([
        bonification.invalidate(id),
        channelUrl ? channelDetail.invalidate(channelUrl) : Promise.resolve(),
        pendingBonifications.invalidate(),
        activeChannels.invalidate(),
        myOffers.invalidate(),
        listing.invalidate(listingId),
      ]);
    const afterBonificationRejected = (id: Bonification['id'], channelUrl?: Channel['channel_url']) =>
      Promise.all([
        bonification.invalidate(id),
        pendingBonifications.invalidate(),
        activeChannels.invalidate(),
        myOffers.invalidate(),
        channelUrl ? channelDetail.invalidate(channelUrl) : Promise.resolve(),
      ]);
    const afterMessageReceived = (channelUrl: string) =>
      Promise.all([activeChannels.invalidate(), channel.invalidate(channelUrl), unreadMessages.invalidate()]);

    return {
      // Listings
      allListings,
      listing,
      allListingLists,
      myOffers,
      myRequests,
      favourites,
      groupingFavourites,
      urgentNeeds,
      listingsWithFilters,
      listingDetailMany,

      // Loans
      allLoans,
      loan,
      ratings,

      // Channels / Messages
      allChannelLists,
      channelList,
      activeChannels,
      archivedChannels,
      channel,
      channelDetail,
      allChannelDetails,
      unreadMessages,

      // User / Profile
      selfUser,
      allUserLists,
      profile,

      // Addresses
      myAddresses,

      // Notifications
      allNotifications,
      unreadNotifications,

      // Bonifications
      bonification,
      pendingBonifications,

      // Preferences
      preference,

      // Statistics
      statistics,

      // Plans
      allPlans,

      // Cross-domain cascades
      afterListingCreated,
      afterListingDeleted,
      afterLoanCreated,
      afterLoanAccepted,
      afterLoanCompleted,
      afterLoanRejected,
      afterAddressChanged,
      afterBonificationCreated,
      afterBonificationAccepted,
      afterBonificationRejected,
      afterMessageReceived,
    };
  })();
}

export function useGetQueryKeys() {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const user = userInfo;
  const { data: allGroups } = useAllGroups(true);
  const { data: allAddresses } = useAllAddresses();

  return (() => {
    // Helper: build a listing query key from filters without needing hooks
    const buildListingQueryKey = (filters: FindAllListingsDTO & { isLoggedIn?: boolean; userLanguage?: Language }) => {
      const normalized = normalizeListingFilters({ ...filters }, allGroups, allAddresses);
      return listingKeys.list('all', { ...normalized });
    };

    const i18nLanguage = getI18nLanguage();

    // Pre-compute common filter-based keys
    const myOffersQueryKey = () =>
      buildListingQueryKey({
        ...getMyListingsOffersFilters(isLoggedIn, user?.id ?? userInfo?.id),
        userLanguage: i18nLanguage,
      });
    const myRequestsQueryKey = () =>
      buildListingQueryKey({
        ...getMyListingsRequestsFilters(isLoggedIn, user?.id ?? userInfo?.id),
        userLanguage: i18nLanguage,
      });

    // === LISTINGS ===
    const allListings = () => listingKeys.all;
    const listing = (id: Listing['id']) => listingKeys.detail(id);
    const listingDetailMany = (ids: Listing['id'][]) => listingKeys.detailMany(ids.join(','));
    const allListingLists = () => listingKeys.lists();
    const myOffers = () => myOffersQueryKey();
    const myRequests = () => myRequestsQueryKey();
    const favourites = () => listingKeys.list('favourites');
    const groupingFavourites = () => listingKeys.list('grouping-favourites');
    const urgentNeeds = () => listingKeys.list('urgent-needs');
    const listingsWithFilters = (filters: FindAllListingsDTO & { isLoggedIn?: boolean; userLanguage?: Language }) =>
      buildListingQueryKey({ userLanguage: i18nLanguage, ...filters });

    // === LOANS ===
    const allLoans = () => loanKeys.all;
    const loan = (id: Loan['id']) => loanKeys.detail(id);
    const ratings = (filters?: { userId?: User['id']; listingId?: Listing['id']; reviewerId?: User['id'] }) =>
      loanKeys.ratings(filters);

    // === CHANNELS / MESSAGES ===
    const allChannelLists = () => channelKeys.lists();
    const channelList = (filters?: {
      search?: string;
      type?: CustomChannelType;
      archived?: boolean;
      listingId?: string;
      userId?: string;
    }) => channelKeys.list(filters);
    const activeChannels = (listingId?: string, userId?: string) =>
      channelKeys.list({ search: undefined, archived: false, userId, listingId });
    const archivedChannels = (listingId?: string, userId?: string) =>
      channelKeys.list({ search: undefined, archived: true, userId, listingId });
    const channel = (url: string) => channelKeys.detailsForOne(url);
    const channelDetail = (url: string) => channelKeys.detail(url, getOneChannelDefaultInclude);
    const allChannelDetails = () => channelKeys.details();
    const unreadMessages = () => messageKeys.unread();

    // === USER / PROFILE ===
    const selfUser = () => userKeys.detail('self');
    const allUserLists = () => userKeys.lists();
    const profile = (userId: Profile['id']) => profileKeys.detail(userId);

    // === ADDRESSES ===
    const myAddresses = () => addressKeys.list('self');

    // === NOTIFICATIONS ===
    const allNotifications = () => notificationKeys.all;
    const unreadNotifications = () => notificationKeys.unread();

    // === BONIFICATIONS ===
    const bonification = (id: Bonification['id']) => bonificationKeys.detail(id);
    const pendingBonifications = () => bonificationKeys.pending();

    // === PREFERENCES ===
    const preference = (name: PreferenceName) => preferencesKeys.detail(name);

    // === STATISTICS ===
    const statistics = (userId: User['id']) => statisticsKeys.detail(userId);

    // === PLANS ===
    const allPlans = () => planKeys.all;

    return {
      // Listings
      allListings,
      listing,
      allListingLists,
      myOffers,
      myRequests,
      favourites,
      groupingFavourites,
      urgentNeeds,
      listingsWithFilters,
      listingDetailMany,

      // Loans
      allLoans,
      loan,
      ratings,

      // Channels / Messages
      allChannelLists,
      channelList,
      activeChannels,
      archivedChannels,
      channel,
      channelDetail,
      allChannelDetails,
      unreadMessages,

      // User / Profile
      selfUser,
      allUserLists,
      profile,

      // Addresses
      myAddresses,

      // Notifications
      allNotifications,
      unreadNotifications,

      // Bonifications
      bonification,
      pendingBonifications,

      // Preferences
      preference,

      // Statistics
      statistics,

      // Plans
      allPlans,
    };
  })();
}
